import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, Star, X, Save } from 'lucide-react';

const AdminTestimonialsPage = () => {
  const { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } = useCMS();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', role: '', company: '', text: '', rating: 5
  });

  useEffect(() => { loadTestimonials(); }, []);

  const loadTestimonials = async () => {
    try {
      const data = await getTestimonials(false);
      setTestimonials(data);
    } catch (e) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateTestimonial(editingId, formData);
        toast.success('Testimonial updated');
      } else {
        await createTestimonial(formData);
        toast.success('Testimonial created');
      }
      resetForm();
      loadTestimonials();
    } catch (e) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await deleteTestimonial(id);
      toast.success('Testimonial deleted');
      loadTestimonials();
    } catch (e) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      role: testimonial.role,
      company: testimonial.company,
      text: testimonial.text,
      rating: testimonial.rating
    });
    setEditingId(testimonial.id);
    setShowForm(true);
  };

  const handleToggleActive = async (testimonial) => {
    try {
      await updateTestimonial(testimonial.id, { is_active: !testimonial.is_active });
      toast.success(`Testimonial ${testimonial.is_active ? 'hidden' : 'shown'}`);
      loadTestimonials();
    } catch (e) {
      toast.error('Failed to update testimonial');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', role: '', company: '', text: '', rating: 5 });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Testimonials</h1>
          <p className="text-text-secondary mt-1">Manage customer testimonials</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Testimonial
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg border-border-line">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Testimonial</CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Role</label>
                    <input
                      type="text"
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Company</label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Testimonial Text</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Rating</label>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="p-1"
                      >
                        <Star 
                          size={24} 
                          className={star <= formData.rating ? 'text-heritage-orange fill-heritage-orange' : 'text-gray-300'} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                    <Save size={18} className="mr-2" />
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Testimonials List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {testimonials.map((testimonial) => (
          <Card 
            key={testimonial.id} 
            className={`border-border-line ${!testimonial.is_active ? 'opacity-50' : ''}`}
          >
            <CardContent className="p-5">
              <div className="flex mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-heritage-orange fill-heritage-orange" size={16} />
                ))}
              </div>
              <p className="text-text-secondary text-sm mb-4 line-clamp-3">"{testimonial.text}"</p>
              <div className="border-t border-border-line pt-3">
                <div className="font-medium text-text-primary">{testimonial.name}</div>
                <div className="text-sm text-text-secondary">{testimonial.role}</div>
                <div className="text-sm text-tech-blue">{testimonial.company}</div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border-line">
                <button
                  onClick={() => handleToggleActive(testimonial)}
                  className={`text-xs px-2 py-1 rounded ${testimonial.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                >
                  {testimonial.is_active ? 'Active' : 'Hidden'}
                </button>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(testimonial)} className="p-2 text-text-secondary hover:text-tech-blue">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(testimonial.id)} className="p-2 text-text-secondary hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          No testimonials yet. Click "Add Testimonial" to create one.
        </div>
      )}
    </div>
  );
};

export default AdminTestimonialsPage;
